// /tenant/payments
import { useEffect, useState } from "react";
import api from "@/lib/api";
import TenantShell from "@/components/TenantShell";
import { Loader2, CheckCircle2, AlertCircle, Clock } from "lucide-react";

function StatusPill({ status }) {
  const map = {
    paid: { c: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
    scheduled: { c: "bg-slate-100 text-slate-600", Icon: Clock },
    late: { c: "bg-red-100 text-red-700", Icon: AlertCircle },
    missed: { c: "bg-red-100 text-red-700", Icon: AlertCircle },
    refunded: { c: "bg-slate-100 text-slate-500", Icon: Clock },
  };
  const { c, Icon } = map[status] || map.scheduled;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${c}`}>
      <Icon className="h-3 w-3" /> {status}
    </span>
  );
}

export default function TenantPayments() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/tenant/payments").then(({ data }) => setData(data)); }, []);
  if (!data) return <TenantShell><div className="flex h-64 items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div></TenantShell>;
  const { payments, total_paid_ytd, currency } = data;
  return (
    <TenantShell>
      <div className="space-y-6" data-testid="tenant-payments-page">
        <header>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Payments</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Payment history</h1>
        </header>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5 shadow-sm sm:col-span-2" data-testid="tenant-payments-ytd">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Paid YTD</p>
            <p className="mt-2 text-3xl font-bold tabular-nums text-emerald-900">{total_paid_ytd} {currency}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Records</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{payments.length}</p>
          </div>
        </div>
        {payments.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="tenant-payments-empty">No payment records yet. Your landlord will populate this ledger.</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm" data-testid="tenant-payments-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">Due</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">Period</th>
                  <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">Paid</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payments.map((p) => (
                  <tr key={p.id} data-testid={`tenant-payment-${p.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-900">{p.due_date}</td>
                    <td className="px-3 py-2 text-slate-500">{p.period_start} → {p.period_end}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{p.amount} {p.currency}</td>
                    <td className="px-3 py-2"><StatusPill status={p.status} /></td>
                    <td className="px-3 py-2 text-slate-500">{p.paid_date ? new Date(p.paid_date).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </TenantShell>
  );
}
